import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { applyTheme } from "@/lib/themes";
import { ShoppingCart, Heart, Search, Star, X, ArrowLeft, Check, Plus, Minus, Package, Sparkles } from "lucide-react";
import ShopNav from "@/components/ShopNav";

const ADULT = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"];
const KIDS = ["2-3 Years", "4-5 Years", "6-7 Years", "8-9 Years", "10-11 Years", "12-13 Years"];
const SORTS = [["featured", "Featured"], ["newest", "Newest"], ["price_asc", "Price: Low"], ["price_desc", "Price: High"], ["popular", "Popular"]];

export default function ShopPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "demo";
  const navigate = useNavigate();
  const [cfg, setCfg] = useState({ categories: [] });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [sort, setSort] = useState("featured");
  const [kids, setKids] = useState("");
  const [inStock, setInStock] = useState(false);
  const [maxPrice, setMaxPrice] = useState("");
  const [wishIds, setWishIds] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [quick, setQuick] = useState(null);

  const refreshMeta = useCallback(() => {
    api.get(`/shop/wishlist?token=${token}`).then((r) => setWishIds(r.data.map((p) => p.id))).catch(() => {});
    api.get(`/shop/cart?token=${token}`).then((r) => setCartCount(r.data.items.reduce((a, i) => a + i.qty, 0))).catch(() => {});
  }, [token]);

  useEffect(() => {
    api.get("/shop/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get(`/employee/me?token=${token}`).then((r) => applyTheme(r.data.theme || "standard")).catch(() => {});
    refreshMeta();
  }, [token, refreshMeta]);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ sort, limit: "60" });
    if (q) p.set("q", q);
    if (category) p.set("category", category);
    if (kids) p.set("kids", kids);
    if (inStock) p.set("in_stock", "true");
    if (maxPrice) p.set("max_price", maxPrice);
    api.get(`/shop/products?${p.toString()}`).then((r) => setProducts(r.data.items)).catch(() => {}).finally(() => setLoading(false));
  }, [q, category, sort, kids, inStock, maxPrice]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const toggleWish = async (pid) => {
    if (wishIds.includes(pid)) { await api.delete(`/shop/wishlist/${pid}?token=${token}`); toast("Removed from wishlist"); }
    else { await api.post(`/shop/wishlist/${pid}?token=${token}`); toast.success("Added to wishlist ❤"); }
    refreshMeta();
  };

  const addToCart = async (prod, variant, qty = 1) => {
    try {
      await api.post(`/shop/cart?token=${token}`, { product_id: prod.id, qty, ...variant });
      toast.success(`${prod.name} added to cart 🛒`);
      refreshMeta();
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add to cart"); return false; }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bq-bg)", color: "var(--bq-text)" }}>
      <ShopNav token={token} cartCount={cartCount} active="shop" />
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="flex items-center gap-2 text-xs uppercase font-mono-bq mb-4" style={{ color: "var(--bq-muted)" }}>
          <button onClick={() => navigate(`/play?token=${token}`)} className="inline-flex items-center gap-1 hover:underline"><ArrowLeft size={12} /> Hub</button>
          <span>/</span><span>Shop</span>{category && <><span>/</span><span>{category}</span></>}
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Filters */}
          <aside className="lg:col-span-1 space-y-4">
            <div className="brutal-card p-4">
              <div className="relative mb-3">
                <Search size={16} className="absolute left-2 top-2.5" style={{ color: "var(--bq-muted)" }} />
                <input data-testid="shop-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" className="brutal-input pl-8 text-sm" />
              </div>
              <label className="text-xs font-black uppercase">Sort</label>
              <select data-testid="shop-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="brutal-input text-sm mb-3">
                {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <label className="text-xs font-black uppercase">Max Price (₹)</label>
              <input data-testid="shop-maxprice" type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="Any" className="brutal-input text-sm mb-3" />
              <label className="flex items-center gap-2 text-sm font-bold mb-1"><input data-testid="shop-instock" type="checkbox" checked={inStock} onChange={(e) => setInStock(e.target.checked)} /> In stock only</label>
              <div className="flex gap-2 mt-2">
                {[["", "All"], ["false", "Adults"], ["true", "Kids"]].map(([v, l]) => (
                  <button key={v} data-testid={`shop-aud-${l.toLowerCase()}`} onClick={() => setKids(v)} className={`px-2 py-1 border-2 themed-border text-xs font-black uppercase ${kids === v ? "themed-shadow-accent" : ""}`} style={{ background: kids === v ? "var(--bq-accent)" : "var(--bq-card-bg)" }}>{l}</button>
                ))}
              </div>
            </div>
            <div className="brutal-card p-4">
              <div className="text-xs font-black uppercase mb-2">Categories</div>
              <div className="flex flex-col gap-1 max-h-72 overflow-y-auto">
                <button data-testid="shop-cat-all" onClick={() => setCategory("")} className={`text-left text-sm px-2 py-1 border-2 ${!category ? "border-black font-black" : "border-transparent"}`}>All Products</button>
                {cfg.categories.map((c) => (
                  <button key={c} data-testid={`shop-cat-${c.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => setCategory(c)} className={`text-left text-sm px-2 py-1 border-2 ${category === c ? "border-black font-black" : "border-transparent hover:border-black/30"}`}>{c}</button>
                ))}
              </div>
            </div>
          </aside>

          {/* Grid */}
          <section className="lg:col-span-3">
            {loading ? (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => <div key={i} className="brutal-card h-80 animate-pulse" style={{ background: "var(--bq-card-bg)" }} />)}
              </div>
            ) : products.length === 0 ? (
              <div className="brutal-card p-12 text-center" data-testid="shop-empty">
                <Package size={48} className="mx-auto mb-3" style={{ color: "var(--bq-muted)" }} />
                <p className="font-black uppercase">No products found</p>
                <p className="text-sm" style={{ color: "var(--bq-muted)" }}>Try clearing filters.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="shop-grid">
                {products.map((p) => (
                  <ProductCard key={p.id} p={p} wished={wishIds.includes(p.id)} onWish={() => toggleWish(p.id)} onQuick={() => setQuick(p)} onAdd={addToCart} />
                ))}
              </div>
            )}
          </section>
        </div>
      </main>

      {quick && <QuickView p={quick} token={token} onClose={() => setQuick(null)} onAdd={addToCart} wished={wishIds.includes(quick.id)} onWish={() => toggleWish(quick.id)} onBuyNow={async (prod, variant, qty) => { const ok = await addToCart(prod, variant, qty); if (ok) navigate(`/shop/checkout?token=${token}`); }} />}
    </div>
  );
}

export function ProductCard({ p, wished, onWish, onQuick, onAdd }) {
  const disc = p.discount_pct > 0;
  return (
    <div data-testid={`product-card-${p.id}`} className="brutal-card overflow-hidden group flex flex-col transition-transform hover:-translate-y-1">
      <div className="relative cursor-pointer" onClick={onQuick}>
        <img src={p.images?.[0]} alt={p.name} className="w-full h-56 object-cover border-b-4 themed-border" />
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {p.featured && <Badge text="Featured" bg="var(--bq-accent)" />}
          {p.is_new && <Badge text="New" bg="var(--bq-accent2)" />}
          {p.limited_edition && <Badge text="Limited" bg="var(--bq-danger)" c="#fff" />}
          {disc && <Badge text={`-${p.discount_pct}%`} bg="#111" c="#fff" />}
        </div>
        <button data-testid={`wish-btn-${p.id}`} onClick={(e) => { e.stopPropagation(); onWish(); }} className="absolute top-2 right-2 w-9 h-9 border-2 themed-border flex items-center justify-center" style={{ background: wished ? "var(--bq-danger)" : "var(--bq-card-bg)" }}>
          <Heart size={16} fill={wished ? "#fff" : "none"} color={wished ? "#fff" : "currentColor"} />
        </button>
        {!p.in_stock && <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.5)" }}><span className="font-black uppercase text-white border-2 border-white px-3 py-1">Out of Stock</span></div>}
      </div>
      <div className="p-3 flex-1 flex flex-col">
        <div className="text-[10px] uppercase font-mono-bq" style={{ color: "var(--bq-muted)" }}>{p.category}</div>
        <h3 className="font-black leading-tight mb-1 line-clamp-2">{p.name}</h3>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-lg font-black">₹{p.price_after_discount}</span>
          {disc && <span className="text-sm line-through" style={{ color: "var(--bq-muted)" }}>₹{p.price}</span>}
        </div>
        <div className="text-xs font-bold mb-2 inline-flex items-center gap-1" style={{ color: "var(--bq-muted)" }}><Star size={11} /> {p.redeem_points} pts to redeem</div>
        {p.colors?.length > 0 && <div className="flex gap-1 mb-2">{p.colors.slice(0, 5).map((c) => <span key={c} title={c} className="w-4 h-4 border-2 border-black rounded-full" style={{ background: colorHex(c) }} />)}</div>}
        <div className="mt-auto grid grid-cols-2 gap-2">
          <button data-testid={`quickview-btn-${p.id}`} onClick={onQuick} className="brutal-btn-secondary text-xs">View</button>
          <button data-testid={`add-cart-btn-${p.id}`} disabled={!p.in_stock} onClick={() => onQuick()} className="brutal-btn text-xs disabled:opacity-40"><ShoppingCart size={12} className="inline" /> Add</button>
        </div>
      </div>
    </div>
  );
}

function Badge({ text, bg, c = "#111" }) {
  return <span className="text-[10px] font-black uppercase px-1.5 py-0.5 border-2 border-black" style={{ background: bg, color: c }}>{text}</span>;
}

export function colorHex(name) {
  const m = { Black: "#111", White: "#fff", Blue: "#1E88E5", Grey: "#9ca3af", Red: "#FF3B30", Navy: "#1e293b", Maroon: "#7f1d1d", Silver: "#cbd5e1" };
  return m[name] || "#ddd";
}

function QuickView({ p, onClose, onAdd, onBuyNow, wished, onWish }) {
  const [forKids, setForKids] = useState(false);
  const [color, setColor] = useState(p.colors?.[0] || null);
  const [adultSize, setAdultSize] = useState(p.adult_sizes?.[0] || null);
  const [kidsSize, setKidsSize] = useState(null);
  const [qty, setQty] = useState(1);
  const isKidsProduct = p.is_kids;
  const showAdult = p.adult_sizes?.length > 0 && !isKidsProduct;
  const showKidsToggle = p.has_kids && !isKidsProduct;
  const variant = () => ({ color, adult_size: isKidsProduct ? null : (showAdult ? adultSize : null), kids_size: isKidsProduct ? kidsSize : (forKids ? kidsSize : null), is_kids: isKidsProduct || forKids });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose} data-testid="quickview-modal">
      <div className="w-full max-w-3xl max-h-[92vh] overflow-y-auto border-4 border-black brutal-shadow" style={{ background: "var(--bq-card-bg)", color: "var(--bq-text)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center border-b-4 border-black p-3 sticky top-0" style={{ background: "var(--bq-card-bg)" }}>
          <h2 className="font-black uppercase truncate">{p.name}</h2>
          <button data-testid="quickview-close" onClick={onClose} className="p-1 border-2 border-black"><X size={16} /></button>
        </div>
        <div className="grid md:grid-cols-2 gap-4 p-4">
          <div>
            <img src={p.images?.[0]} alt={p.name} className="w-full h-72 object-cover border-4 themed-border" />
            {p.images?.length > 1 && <div className="flex gap-2 mt-2">{p.images.map((im, i) => <img key={i} src={im} className="w-14 h-14 object-cover border-2 border-black" alt="" />)}</div>}
          </div>
          <div className="space-y-3">
            <div className="text-xs uppercase font-mono-bq" style={{ color: "var(--bq-muted)" }}>{p.category}</div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-black">₹{p.price_after_discount}</span>
              {p.discount_pct > 0 && <span className="line-through" style={{ color: "var(--bq-muted)" }}>₹{p.price}</span>}
              {p.discount_pct > 0 && <span className="text-xs font-black" style={{ color: "var(--bq-danger)" }}>{p.discount_pct}% OFF</span>}
            </div>
            <div className="text-sm font-bold inline-flex items-center gap-1"><Star size={13} /> {p.redeem_points} points to fully redeem</div>
            <div className="text-xs font-bold" style={{ color: p.in_stock ? "green" : "var(--bq-danger)" }}>{p.in_stock ? `In stock (${p.available_qty} available)` : "Out of stock"}</div>
            <p className="text-sm" style={{ color: "var(--bq-muted)" }}>{p.description}</p>

            {p.colors?.length > 0 && (
              <div>
                <div className="text-xs font-black uppercase mb-1">Color: {color}</div>
                <div className="flex gap-2">{p.colors.map((c) => <button key={c} data-testid={`qv-color-${c}`} onClick={() => setColor(c)} className={`w-8 h-8 border-2 rounded-full ${color === c ? "border-black ring-2 ring-offset-1" : "border-black/40"}`} style={{ background: colorHex(c) }} title={c} />)}</div>
              </div>
            )}

            {showAdult && (
              <div>
                <div className="text-xs font-black uppercase mb-1">Size</div>
                <div className="flex flex-wrap gap-1">{p.adult_sizes.map((s) => <button key={s} data-testid={`qv-size-${s}`} onClick={() => setAdultSize(s)} className={`px-2 py-1 border-2 border-black text-xs font-black ${adultSize === s ? "themed-shadow-accent" : ""}`} style={{ background: adultSize === s ? "var(--bq-accent)" : "var(--bq-card-bg)" }}>{s}</button>)}</div>
              </div>
            )}

            {showKidsToggle && (
              <label className="flex items-center gap-2 text-sm font-bold border-2 border-black p-2">
                <input data-testid="qv-forkids" type="checkbox" checked={forKids} onChange={(e) => { setForKids(e.target.checked); setKidsSize(e.target.checked ? KIDS[0] : null); }} /> Buy for your Kids
              </label>
            )}
            {(isKidsProduct || forKids) && (
              <div>
                <div className="text-xs font-black uppercase mb-1">Kids Size</div>
                <div className="flex flex-wrap gap-1">{KIDS.map((s) => <button key={s} data-testid={`qv-kidsize-${s}`} onClick={() => setKidsSize(s)} className={`px-2 py-1 border-2 border-black text-xs font-black ${kidsSize === s ? "themed-shadow-accent" : ""}`} style={{ background: kidsSize === s ? "var(--bq-accent)" : "var(--bq-card-bg)" }}>{s}</button>)}</div>
              </div>
            )}

            <div className="flex items-center gap-2">
              <span className="text-xs font-black uppercase">Qty</span>
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-8 h-8 border-2 border-black"><Minus size={12} className="mx-auto" /></button>
              <span data-testid="qv-qty" className="font-black w-8 text-center">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="w-8 h-8 border-2 border-black"><Plus size={12} className="mx-auto" /></button>
            </div>

            <div className="grid grid-cols-2 gap-2 pt-2">
              <button data-testid="qv-add-cart" disabled={!p.in_stock} onClick={() => onAdd(p, variant(), qty)} className="brutal-btn-secondary disabled:opacity-40"><ShoppingCart size={14} className="inline mr-1" />Add to Cart</button>
              <button data-testid="qv-buy-now" disabled={!p.in_stock} onClick={() => onBuyNow(p, variant(), qty)} className="brutal-btn disabled:opacity-40"><Sparkles size={14} className="inline mr-1" />Buy Now</button>
            </div>
            <button data-testid="qv-wish" onClick={onWish} className="w-full brutal-btn-secondary text-sm"><Heart size={13} className="inline mr-1" fill={wished ? "currentColor" : "none"} />{wished ? "In Wishlist" : "Add to Wishlist"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
